"use client";
import { motion, Variants } from "framer-motion";
import { Container, SectionSubHeading, SectionTitle } from "@/components";
import { TeamEndorsement } from "@/lib/types";

interface TeamEndorsementsProps {
  name: string;
  endorsements: TeamEndorsement[];
}

const card: Variants = {
  hidden: { opacity: 0, y: 26 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

// What the rest of the crew says about this member — internal social proof,
// driven by `endorsements`. Rendered only when the data exists.
const TeamEndorsements = ({ name, endorsements }: TeamEndorsementsProps) => {
  const first = name.split(" ")[0];

  return (
    <section className="bg-background py-14 md:py-20">
      <Container className="px-4 lg:px-10">
        <div className="space-y-3">
          <SectionSubHeading subtitle="In Their Words" className="text-primary" />
          <SectionTitle
            title={
              <>
                WHAT THE CREW <br /> SAYS ABOUT {first.toUpperCase()}.
              </>
            }
          />
        </div>

        <div className="mt-14 grid gap-6 md:mt-20 md:grid-cols-3">
          {endorsements.map((item, i) => (
            <motion.figure
              key={item.by}
              variants={card}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className="flex flex-col rounded-2xl border-2 border-black bg-white p-7 shadow-[6px_6px_0_0_rgba(0,0,0,1)]"
            >
              <span
                aria-hidden
                className="font-black leading-none text-6xl text-primary"
              >
                &ldquo;
              </span>
              <blockquote className="-mt-4 flex-1 text-lg font-medium leading-snug text-foreground">
                {item.quote}
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3 border-t-2 border-black/10 pt-5">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-black text-sm font-black text-white">
                  {item.by.charAt(0)}
                </span>
                <span>
                  <span className="block text-sm font-black uppercase tracking-tight">
                    {item.by}
                  </span>
                  <span className="block text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                    {item.role}
                  </span>
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </Container>
    </section>
  );
};

export default TeamEndorsements;
